import * as React from "react";
import dayjs from "dayjs";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/*
 * Thin time picker — native `<input type="time">` styled to match the
 * shadcn Input primitive. Used inside dialogs to replace the MUI X
 * TimePicker (which dragged in @mui/x-date-pickers + locale providers).
 *
 * The value is a Day.js instance for parity with the rest of the codebase
 * (DatePicker, dynamic-field-value/date use Day.js too). Step is set to
 * 15 minutes to match the legacy behaviour. Native input handles AM/PM
 * vs 24h via the OS locale, which is the right default.
 */

interface TimeInputProps {
  value?: dayjs.Dayjs | null;
  /** Receives a Day.js instance whose date matches `value`'s but with the
   *  hour/minute set to the picked time. Null when the user clears. */
  onChange: (next: dayjs.Dayjs | null) => void;
  label?: React.ReactNode;
  /** Step in seconds. Defaults to 900 (15 minutes) to match the legacy
   *  MUI TimePicker `minutesStep={15}`. */
  step?: number;
  disabled?: boolean;
  required?: boolean;
  /** Truthy = destructive ring (matches AutocompleteInput convention). */
  error?: unknown;
  className?: string;
}

export function TimeInput({
  value,
  onChange,
  label,
  step = 900,
  disabled,
  required,
  error,
  className,
}: TimeInputProps) {
  const hasError = Boolean(error);

  // Convert Day.js → "HH:mm" for the native input value.
  const displayValue = React.useMemo(() => {
    if (!value || !value.isValid?.()) return "";
    return value.format("HH:mm");
  }, [value]);

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (!raw) {
        onChange(null);
        return;
      }
      const [hStr, mStr] = raw.split(":");
      const h = Number(hStr);
      const m = Number(mStr);
      if (Number.isNaN(h) || Number.isNaN(m)) {
        onChange(null);
        return;
      }
      // Preserve the date portion of the existing value if any, so that
      // editing time alone doesn't reset to today.
      const base = value && value.isValid?.() ? value : dayjs();
      onChange(base.hour(h).minute(m).second(0));
    },
    [onChange, value],
  );

  return (
    <div className={cn("space-y-1", className)}>
      {label ? (
        <Label
          className={cn(
            "text-sm font-medium",
            required && "after:ml-0.5 after:text-destructive after:content-['*']",
          )}
        >
          {label}
        </Label>
      ) : null}
      <Input
        type="time"
        value={displayValue}
        onChange={handleChange}
        step={step}
        disabled={disabled}
        aria-invalid={hasError}
        className={cn(hasError && "border-destructive ring-destructive/20")}
      />
    </div>
  );
}
